import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

import { ProcedureContext } from "@/context/ProcedureContext";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
import ArrowRightOutlinedIcon from "@mui/icons-material/ArrowRightOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Chip,
	Divider,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Skeleton,
	Stack,
	Switch,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import { ProcedureGroupItem } from "@/components/Procedures/ProcedureGroupItem";

import { CustomTreeView } from "@/helpers/components/TreeView/CustomTreeView";

import { Procedure } from "@/model/Procedure/Procedure";
import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";

import {
	ProcedureQueryKeys,
	useFetchProcedures,
} from "@/service/ProcedureQueries";

/* ---------------------------------- types ---------------------------------- */

interface IAccordionDesign {
	groupList: ProcedureGroup[];
	SetGroupList: React.Dispatch<React.SetStateAction<ProcedureGroup[]>>;
	/** when true, open everything (also applied when search clears) */
	expandAll: boolean;
}

/* ------------------------------- small helpers ------------------------------ */

/** Debounce a value to keep typing smooth */
function useDebounced<T>(value: T, ms = 250) {
	const [v, setV] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setV(value), ms);
		return () => clearTimeout(t);
	}, [value, ms]);
	return v;
}

/** Remove diacritics + lowercase for robust matching */
const norm = (s?: string) =>
	(s ?? "")
		.normalize("NFD")
		// @ts-expect-error: unicode prop escape supported in modern runtimes
		.replace(/\p{Diacritic}/gu, "")
		.toLowerCase();

/** Escape regex special chars */
const escapeRe = (s: string) => s.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

/** Tokenize query (AND logic over tokens) */
const makeTokens = (q: string) =>
	norm(q)
		.split(/\s+/)
		.map((t) => t.trim())
		.filter(Boolean);

/** Does hay include ALL tokens? */
const matchAll = (hay: string, tokens: string[]) =>
	tokens.every((t) => hay.includes(t));

/** A tiny debounce for side effects (e.g., localStorage) */
const useDebouncedEffect = (fn: () => void, deps: any[], ms: number) => {
	useEffect(() => {
		const t = setTimeout(fn, ms);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps);
};

/* ------------------------------ Highlighter UI ------------------------------ */
/** Token-aware multi-hit highlighter */
const Highlight: FC<{ text?: string; tokens: string[] }> = ({
	text = "",
	tokens,
}) => {
	if (!tokens.length) return <>{text}</>;
	const source = text;
	const normed = norm(source);
	// Build a regex that matches any token (on the normalized string)
	const pattern = new RegExp(tokens.map(escapeRe).join("|"), "gi");

	// We need indices of matches on the normalized string, but we’ll slice the original.
	const parts: Array<{ start: number; end: number; match: string }> = [];
	let m: RegExpExecArray | null;
	// Walk matches; derive the original-substring bounds using the same length
	while ((m = pattern.exec(normed))) {
		const match = m[0];
		const start = m.index;
		const end = start + match.length;
		parts.push({ start, end, match });
	}
	if (!parts.length) return <>{text}</>;

	// Reconstruct from original string using indices
	const out: React.ReactNode[] = [];
	let last = 0;
	parts.forEach((p, idx) => {
		if (p.start > last) out.push(source.slice(last, p.start));
		out.push(
			<mark
				key={idx}
				style={{
					padding: "1px 4px",
					margin: "0 1px",
					borderRadius: "4px",
					backgroundColor: "rgba(195, 124, 89, 0.25)", // warm beige tint
					color: "#000", // dark text like your screenshot
					fontWeight: 700,
				}}
			>
				{source.slice(p.start, p.end)}
			</mark>,
		);
		last = p.end;
	});
	if (last < source.length) out.push(source.slice(last));

	return <>{out}</>;
};

/* --------------------------------- Component -------------------------------- */

export const AccordionDesign: FC<IAccordionDesign> = ({
	groupList,
	SetGroupList, // unused here but preserved for API parity
	expandAll,
}) => {
	const theme = useTheme();
	const { openFolders, setOpenFolders } = useContext(ProcedureContext);

	// UI State
	const [selected, setSelected] = useState<string>("");
	const [rawQ, setRawQ] = useState<string>("");
	const q = useDebounced(rawQ, 250);
	const tokens = useMemo(() => makeTokens(q), [q]);
	const hasQuery = tokens.length > 0;

	const [onlyMatches, setOnlyMatches] = useState<boolean>(false);

	const { isLoading: isProceduresLoading, data: procedures = [] } =
		useFetchProcedures(ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc);

	/* ---------------------------- Precompute lookups --------------------------- */

	// Normalize once; attach to objects without mutating sources (use side maps)
	const normGroupName = useMemo(() => {
		const m = new Map<number, string>();
		for (const g of groupList) m.set(g.Id, norm(g.Name));
		return m;
	}, [groupList]);

	const normProcName = useMemo(() => {
		const m = new Map<number, string>();
		for (const p of procedures as Procedure[]) m.set(p.Id, norm(p.Name));
		return m;
	}, [procedures]);

	const parentById = useMemo(() => {
		const m = new Map<number, number>();
		for (const g of groupList) m.set(g.Id, g.ParentId ?? 0);
		return m;
	}, [groupList]);

	const childrenByParent = useMemo(() => {
		const m = new Map<number, ProcedureGroup[]>();
		for (const g of groupList) {
			const pid = g.ParentId ?? 0;
			if (!m.has(pid)) m.set(pid, []);
			m.get(pid)!.push(g);
		}
		return m;
	}, [groupList]);

	const proceduresByGroup = useMemo(() => {
		const m = new Map<number, Procedure[]>();
		for (const p of procedures as Procedure[]) {
			const gid = p.GroupId ?? 0;
			if (!m.has(gid)) m.set(gid, []);
			m.get(gid)!.push(p);
		}
		return m;
	}, [procedures]);

	const allGroupIds = useMemo(
		() => groupList.map((g) => String(g.Id)),
		[groupList],
	);

	const rootGroups = useMemo(
		() => groupList.filter((g) => (g.ParentId ?? 0) === 0),
		[groupList],
	);

	/* ---------------------------- Matching functions --------------------------- */

	const matchGroup = (g: ProcedureGroup) =>
		!hasQuery || matchAll(normGroupName.get(g.Id) ?? "", tokens);

	const matchProc = (p: Procedure) =>
		!hasQuery ||
		matchAll(normProcName.get(p.Id) ?? "", tokens) ||
		// allow id matching via tokens (AND across tokens)
		tokens.every((t) => String(p.Id).includes(t));

	/* ----------------------- Visibility (pruned, no stubs) --------------------- */

	/**
	 * Strategy:
	 * 1) Compute base matches (groups/procs).
	 * 2) Seed visibility with:
	 *    - matched procedures (visibleProcedureIds)
	 *    - matched groups (visibleGroupIdsSeed)
	 * 3) If NOT onlyMatches, include all procedures in any matched group's subtree.
	 * 4) Make all ancestors of visible nodes visible.
	 * 5) Prune groups that end up with no visible descendants (no empty ancestor stubs).
	 */
	const { visibleGroupIds, visibleProcedureIds, folderCount, procCount } =
		useMemo(() => {
			if (!hasQuery) {
				return {
					visibleGroupIds: new Set<number>(),
					visibleProcedureIds: new Set<number>(),
					folderCount: 0,
					procCount: 0,
				};
			}

			const visibleG = new Set<number>();
			const visibleP = new Set<number>();

			// 1) Base matches
			const matchedGroups = groupList.filter(matchGroup).map((g) => g.Id);
			const matchedProcedures = (procedures as Procedure[]).filter(matchProc);

			// 2) Seed with matched procedures
			for (const p of matchedProcedures) visibleP.add(p.Id);

			// 3) If a group matches:
			//    - include group
			//    - include subtree groups
			//    - include subtree procedures only if !onlyMatches
			const pushSubtree = (gid: number) => {
				const stack = [gid];
				const seen = new Set<number>();
				while (stack.length) {
					const id = stack.pop()!;
					if (seen.has(id)) continue;
					seen.add(id);
					visibleG.add(id);
					if (!onlyMatches) {
						for (const p of proceduresByGroup.get(id) ?? []) visibleP.add(p.Id);
					}
					for (const child of childrenByParent.get(id) ?? [])
						stack.push(child.Id);
				}
			};
			matchedGroups.forEach(pushSubtree);

			// 4) Ancestors of procedures/groups should be visible
			const includeAncestors = (gid: number) => {
				let cur = gid;
				const seen = new Set<number>();
				while (cur && !seen.has(cur)) {
					seen.add(cur);
					visibleG.add(cur);
					cur = parentById.get(cur) ?? 0;
				}
			};
			for (const p of visibleP) {
				const proc = (procedures as Procedure[]).find((x) => x.Id === p);
				if (proc) includeAncestors(proc.GroupId ?? 0);
			}
			for (const g of Array.from(visibleG)) includeAncestors(g);

			// 5) Prune: remove any group that has no visible descendant content
			//    A group stays if:
			//      - it is matched itself, OR
			//      - it has any visible child group after pruning, OR
			//      - it has any visible procedure inside it (direct children suffice)
			//    We'll compute bottom-up using a DFS post-order.
			const childrenMap = childrenByParent; // alias
			const procMap = proceduresByGroup;

			const keep = new Set<number>();
			const seen = new Set<number>();

			const hasVisibleContent = (gid: number): boolean => {
				if (seen.has(gid)) return keep.has(gid);
				seen.add(gid);

				let content = false;

				// Direct procedures
				const procs = procMap.get(gid) ?? [];
				if (procs.some((p) => visibleP.has(p.Id))) content = true;

				// Child groups
				for (const child of childrenMap.get(gid) ?? []) {
					if (visibleG.has(child.Id) && hasVisibleContent(child.Id)) {
						content = true;
					}
				}

				// Also keep if the group itself matched initially
				if (matchedGroups.includes(gid)) content = true;

				if (content) keep.add(gid);
				return content;
			};

			for (const g of Array.from(visibleG)) hasVisibleContent(g);

			// Final pruned set
			const prunedGroups = new Set<number>();
			for (const g of Array.from(visibleG))
				if (keep.has(g)) prunedGroups.add(g);

			return {
				visibleGroupIds: prunedGroups,
				visibleProcedureIds: visibleP,
				folderCount: prunedGroups.size,
				procCount: visibleP.size,
			};
		}, [
			hasQuery,
			tokens,
			groupList,
			procedures,
			childrenByParent,
			parentById,
			proceduresByGroup,
			onlyMatches,
			matchGroup,
			matchProc,
		]);

	/* ---------------------------- Expand/collapse sync ------------------------- */

	const toSortedKey = (ids: string[]) =>
		Array.from(new Set(ids)).sort().join("|");
	const setToSortedKey = (s: Set<number> | Set<string>) =>
		Array.from(s as Set<any>)
			.map(String)
			.sort()
			.join("|");

	const prevOpenBeforeSearch = useRef<string[] | null>(null);

	// Persist expanded with debounce
	useDebouncedEffect(
		() => {
			localStorage.setItem("proc.openFolders", JSON.stringify(openFolders));
		},
		[openFolders],
		300,
	);

	// Hydrate from persisted once
	useEffect(() => {
		const saved = localStorage.getItem("proc.openFolders");
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				if (Array.isArray(parsed)) setOpenFolders(parsed.map(String));
			} catch {}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Respect external expandAll prop (open all / close all)
	useEffect(() => {
		if (!groupList.length) return;
		if (expandAll) {
			const next = groupList.map((g) => String(g.Id));
			const nextKey = toSortedKey(next);
			const curKey = toSortedKey(openFolders);
			if (nextKey !== curKey) setOpenFolders(next);
		}
		// if expandAll becomes false, we do not forcibly collapse—caller can control if needed
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [expandAll, groupList]);

	// Auto-expand during search; restore after clear
	useEffect(() => {
		if (hasQuery) {
			if (prevOpenBeforeSearch.current === null)
				prevOpenBeforeSearch.current = openFolders.slice();

			const target = new Set<string>();
			visibleGroupIds.forEach((gid) => {
				let cur = gid;
				const seen = new Set<number>();
				while (cur && !seen.has(cur)) {
					seen.add(cur);
					target.add(String(cur));
					const parent = parentById.get(cur) ?? 0;
					if (!parent || parent === cur) break;
					cur = parent;
				}
			});
			const targetKey = setToSortedKey(target);
			const currentKey = toSortedKey(openFolders);
			if (targetKey !== currentKey) setOpenFolders(Array.from(target));
			return;
		}

		// restore on clear
		if (prevOpenBeforeSearch.current !== null) {
			const restore = prevOpenBeforeSearch.current;
			prevOpenBeforeSearch.current = null;
			const restoreKey = toSortedKey(restore);
			const currentKey = toSortedKey(openFolders);
			if (restoreKey !== currentKey) setOpenFolders(restore);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasQuery, visibleGroupIds, openFolders, setOpenFolders, parentById]);

	/* --------------------------------- handlers -------------------------------- */

	const handleToggle = (_e: React.SyntheticEvent, nodeIds: string[]) => {
		const nextKey = toSortedKey(nodeIds);
		const curKey = toSortedKey(openFolders);
		if (nextKey !== curKey) setOpenFolders(nodeIds);
	};

	const handleSelect = (_e: React.SyntheticEvent, nodeId: string) => {
		setSelected(nodeId);
	};

	const allOpen = useMemo(() => {
		const allKey = toSortedKey(allGroupIds);
		const curKey = toSortedKey(openFolders);
		return allKey.length > 0 && curKey === allKey;
	}, [allGroupIds, openFolders]);

	const toggleAll = () => setOpenFolders(allOpen ? [] : allGroupIds);

	// Keyboard: / to focus search, Esc to clear
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const activeTag = (document.activeElement as HTMLElement | null)?.tagName;
			const isTyping =
				activeTag === "INPUT" ||
				activeTag === "TEXTAREA" ||
				(document.activeElement as any)?.isContentEditable;

			if (
				(e.key === "/" ||
					(e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey))) &&
				!isTyping
			) {
				e.preventDefault();
				document.getElementById("proc-search")?.focus();
			} else if (e.key === "Escape" && rawQ) {
				setRawQ("");
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [rawQ]);

	/* ------------------------------ empty/derived UI --------------------------- */

	// Filter roots when searching using the PRUNED visibleGroupIds set
	const rootToRender = hasQuery
		? rootGroups.filter((g) => visibleGroupIds.has(g.Id))
		: rootGroups;

	// Empty state
	if (!groupList.length) {
		return (
			<Box
				sx={{
					bgcolor: "background.paper",
					border: `1px solid ${theme.palette.divider}`,
					borderRadius: 2,
					boxShadow: 1,
					p: 3,
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					color: "text.secondary",
				}}
			>
				<FolderOutlinedIcon />
				<Typography variant="body2">No folders available.</Typography>
			</Box>
		);
	}

	const renderLoading = (
		<Stack spacing={1} sx={{ p: 2 }}>
			{Array.from({ length: 6 }).map((_, i) => (
				<Skeleton key={i} height={24} />
			))}
		</Stack>
	);

	/* ---------------------------------- render --------------------------------- */

	return (
		<Box className="accordion-wrapper">
			{/* Toolbar */}
			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={1}
				alignItems="center"
				justifyContent="space-between"
				sx={{
					px: 1,
					py: 1,
					position: "sticky",
					top: 0,
					zIndex: 1,
					bgcolor: "background.paper",
				}}
			>
				<TextField
					id="proc-search"
					size="small"
					placeholder="Search folders & procedures…"
					value={rawQ}
					onChange={(e) => setRawQ(e.target.value)}
					sx={{ flex: 1, minWidth: 260 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
						endAdornment: !!rawQ && (
							<InputAdornment position="end">
								<IconButton
									aria-label="Clear search"
									onClick={() => setRawQ("")}
									size="small"
								>
									<span aria-hidden>✕</span>
								</IconButton>
							</InputAdornment>
						),
					}}
				/>

				<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
					<Tooltip
						title={
							onlyMatches
								? "Showing only items that directly match your search."
								: "Including non-matching children inside matched folders."
						}
						arrow
					>
						<FormControlLabel
							control={
								<Switch
									checked={onlyMatches}
									onChange={() => setOnlyMatches((v) => !v)}
									color="primary"
								/>
							}
						/>
					</Tooltip>

					{hasQuery && (
						<Chip
							size="small"
							variant="outlined"
							label={`${folderCount} ${folderCount === 1 ? "folder" : "folders"} • ${procCount} ${procCount === 1 ? "procedure" : "procedures"}`}
							sx={{ pointerEvents: "none" }}
						/>
					)}

					<Tooltip title={allOpen ? "Collapse all" : "Expand all"}>
						<span>
							<Button
								size="small"
								variant="outlined"
								onClick={toggleAll}
								startIcon={
									allOpen ? (
										<ExpandLessIcon fontSize="small" />
									) : (
										<ExpandMoreIcon fontSize="small" />
									)
								}
								sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
							>
								{allOpen ? "Collapse" : "Expand"}
							</Button>
						</span>
					</Tooltip>
				</Stack>
			</Stack>

			<Divider />

			{/* Scrollable tree area */}
			<Box
				className="wrapper-scroll"
				sx={{
					maxHeight: { xs: "55vh", md: "70vh" },
					overflowY: "auto",
					py: 0.5,
					// nicer scrollbar
					"&::-webkit-scrollbar": { width: 8 },
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: alpha(theme.palette.text.primary, 0.2),
						borderRadius: 8,
					},
					"&:hover::-webkit-scrollbar-thumb": {
						backgroundColor: alpha(theme.palette.text.primary, 0.35),
					},
				}}
			>
				{isProceduresLoading ? (
					renderLoading
				) : hasQuery && rootToRender.length === 0 ? (
					<Box sx={{ p: 2, color: "text.secondary" }}>
						<Typography variant="body2">
							No results. Try different keywords or turn off “Only matches”.
						</Typography>
					</Box>
				) : (
					<CustomTreeView
						dense
						defaultCollapseIcon={<ArrowDropDownOutlinedIcon />}
						defaultExpandIcon={<ArrowRightOutlinedIcon />}
						onNodeToggle={handleToggle}
						expanded={openFolders}
						selected={selected}
						multiSelect={false}
						onNodeSelect={handleSelect}
					>
						{rootToRender.map((group, i) => (
							<ProcedureGroupItem
								key={group.Id}
								group={group}
								root
								groupList={groupList}
								allProcedures={
									isProceduresLoading ? [] : (procedures as Procedure[])
								}
								SetProcedureGroupList={SetGroupList}
								i={i}
								expanded={openFolders}
								// pass filters down
								searchQuery={q.trim().toLowerCase()}
								visibleGroupIds={hasQuery ? visibleGroupIds : undefined}
								visibleProcedureIds={hasQuery ? visibleProcedureIds : undefined}
								onlyMatches={onlyMatches}
								// token-aware multi-hit highlight
								renderProcedureLabel={(name: string) => (
									<Highlight text={name} tokens={tokens} />
								)}
							/>
						))}
					</CustomTreeView>
				)}
			</Box>
		</Box>
	);
};
